import React from 'react';
import PropTypes from 'prop-types';
import './Badge.css';

/**
 * Componente Badge moderno seguindo a identidade visual Mexx
 * Usado para status, notificações e categorizações
 */
const Badge = ({
  children,
  variant = 'default',
  size = 'medium',
  shape = 'rounded',
  dot = false,
  pulse = false,
  icon = null,
  className = '',
  ...props
}) => {
  const baseClass = 'mexx-badge';
  const variantClass = `mexx-badge--${variant}`;
  const sizeClass = `mexx-badge--${size}`;
  const shapeClass = `mexx-badge--${shape}`;
  const dotClass = dot ? 'mexx-badge--dot' : '';
  const pulseClass = pulse ? 'mexx-badge--pulse' : '';

  const badgeClass = [
    baseClass,
    variantClass,
    sizeClass,
    shapeClass,
    dotClass,
    pulseClass,
    className
  ].filter(Boolean).join(' ');

  // Se for apenas um dot, não renderiza texto
  if (dot) {
    return (
      <span className={badgeClass} {...props}>
        {pulse && <span className="mexx-badge__pulse-ring" />}
      </span>
    );
  }

  return (
    <span className={badgeClass} {...props}>
      {pulse && <span className="mexx-badge__pulse-ring" />}

      {/* Ícone */}
      {icon && (
        <span className="mexx-badge__icon">
          {icon}
        </span>
      )}

      {/* Conteúdo */}
      {children && (
        <span className="mexx-badge__content">
          {children}
        </span>
      )}
    </span>
  );
};

/**
 * Badge com contador - para notificações numéricas
 */
const BadgeCounter = ({
  count = 0,
  max = 99,
  showZero = false,
  ...props
}) => {
  const displayCount = count > max ? `${max}+` : count.toString();

  if (count === 0 && !showZero) {
    return null;
  }

  return (
    <Badge {...props}>
      {displayCount}
    </Badge>
  );
};

/**
 * Badge de status - para estados específicos
 */
const BadgeStatus = ({
  status = 'default',
  text = '',
  showDot = true,
  ...props
}) => {
  const statusVariants = {
    online: 'success',
    offline: 'neutral',
    away: 'warning',
    busy: 'error',
    active: 'success',
    inactive: 'neutral',
    pending: 'warning',
    approved: 'success',
    rejected: 'error',
    draft: 'neutral'
  };

  const variant = statusVariants[status] || 'default';

  if (showDot && !text) {
    return (
      <Badge
        variant={variant}
        dot
        {...props}
      />
    );
  }

  return (
    <Badge
      variant={variant}
      icon={showDot ? <span className="mexx-badge__status-dot" /> : null}
      {...props}
    >
      {text || status}
    </Badge>
  );
};

Badge.propTypes = {
  children: PropTypes.node,
  variant: PropTypes.oneOf([
    'default',
    'primary',
    'success',
    'warning',
    'error',
    'info',
    'neutral'
  ]),
  size: PropTypes.oneOf(['small', 'medium', 'large']),
  shape: PropTypes.oneOf(['rounded', 'square', 'pill']),
  dot: PropTypes.bool,
  pulse: PropTypes.bool,
  icon: PropTypes.node,
  className: PropTypes.string
};

BadgeCounter.propTypes = {
  count: PropTypes.number,
  max: PropTypes.number,
  showZero: PropTypes.bool
};

BadgeStatus.propTypes = {
  status: PropTypes.oneOf([
    'default',
    'online',
    'offline',
    'away',
    'busy',
    'active',
    'inactive',
    'pending',
    'approved',
    'rejected',
    'draft'
  ]),
  text: PropTypes.string,
  showDot: PropTypes.bool
};

// Export named components
Badge.Counter = BadgeCounter;
Badge.Status = BadgeStatus;

export default Badge;